<template>
  <button
    type="button"
    class="btn btn-secondary btn-medium"
    aria-label="Изменить пароль"
    @click="open"
  >
    Изменить пароль
  </button>

  <a-modal
    v-model:open="isOpen"
    class="password-change-lk-modal"
    :footer="null"
    :closable="true"
    :mask-closable="false"
    :keyboard="false"
    @cancel="close"
    centered
    width="456"
  >
    <div class="password-change-lk-modal__header">
      <h3 class="password-change-lk-modal__title">Изменить пароль</h3>
      <p class="password-change-lk-modal__description">
        Придумайте пароль из 6-10 символов.
        <br />
        Используйте цифры, заглавные и строчные буквы.
      </p>
    </div>

    <a-form
      ref="formRef"
      class="password-change-lk-modal__form stc-ant-field-skin"
      layout="vertical"
      :required-mark="false"
      :model="form"
      :rules="rules"
      @finish="onSubmit"
    >
      <a-form-item name="oldPassword" label="Текущий пароль">
        <a-input-password
          v-model:value="form.oldPassword"
          size="large"
          placeholder="Введите текущий пароль"
          autocomplete="current-password"
          :icon-render="renderPasswordIcon"
        />
      </a-form-item>

      <p v-if="oldPasswordError" class="password-change-lk-modal__hint is-error">
        <span class="password-change-lk-modal__hint-icon" aria-hidden="true">
          <svg
            class="password-change-lk-modal__hint-mark"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="7" cy="7" r="7" fill="var(--stc-red-600)" />
            <path
              d="M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75"
              stroke="#fff"
              stroke-width="1.35"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <span>{{ oldPasswordError }}</span>
      </p>

      <a-form-item name="password" label="Новый пароль">
        <a-input-password
          v-model:value="form.password"
          size="large"
          placeholder="Введите новый пароль"
          autocomplete="new-password"
          :icon-render="renderPasswordIcon"
        />
      </a-form-item>

      <p
        v-if="showPasswordStrengthHint"
        class="password-change-lk-modal__hint"
        :class="passwordStrengthClass"
      >
        <span class="password-change-lk-modal__hint-icon" aria-hidden="true">
          <svg
            v-if="isPasswordStrong"
            class="password-change-lk-modal__hint-mark"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="7" cy="7" r="7" fill="#52c41a" />
            <path
              d="M3.5 7 L6 9.5 L10.5 4"
              stroke="#fff"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            class="password-change-lk-modal__hint-mark"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="7" cy="7" r="7" fill="var(--stc-red-600)" />
            <path
              d="M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75"
              stroke="#fff"
              stroke-width="1.35"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <span>{{ passwordStrengthText }}</span>
      </p>

      <a-form-item name="confirmPassword" label="Подтвердить пароль">
        <a-input-password
          v-model:value="form.confirmPassword"
          size="large"
          placeholder="Подтвердите пароль"
          autocomplete="new-password"
          :icon-render="renderPasswordIcon"
        />
      </a-form-item>

      <p
        v-if="showPasswordsMatchHint"
        class="password-change-lk-modal__hint"
        :class="passwordsMatchClass"
      >
        <span class="password-change-lk-modal__hint-icon" aria-hidden="true">
          <svg
            v-if="isPasswordsMatch"
            class="password-change-lk-modal__hint-mark"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="7" cy="7" r="7" fill="#52c41a" />
            <path
              d="M3.5 7 L6 9.5 L10.5 4"
              stroke="#fff"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            class="password-change-lk-modal__hint-mark"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="7" cy="7" r="7" fill="var(--stc-red-600)" />
            <path
              d="M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75"
              stroke="#fff"
              stroke-width="1.35"
              stroke-linecap="round"
            />
          </svg>
        </span>
        <span>{{ passwordsMatchText }}</span>
      </p>

      <button
        type="submit"
        class="password-change-lk-modal__submit btn btn-primary"
        :disabled="!canSubmit || submitLoading"
      >
        Сохранить
      </button>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, reactive, ref, h, watch } from 'vue';
import { inject } from 'vue';
import type { Rule } from 'ant-design-vue/es/form';

import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { updatePasswordChangedInfo } from '@/js/utils/update-password-changed-info';

const passwordSettingsRoot = inject<ParentNode>('passwordSettingsRoot', document);

const CHANGE_PASSWORD_ENDPOINT = '/api/auth/change-password-lk/';
const OLD_PASSWORD_ERROR_MESSAGE = 'Неверный пароль';
const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const isOpen = ref(false);
const submitLoading = ref(false);
const oldPasswordError = ref('');
const form = reactive({
  oldPassword: '',
  password: '',
  confirmPassword: '',
});

const rules: Record<string, Rule[]> = {
  oldPassword: [{ required: true, message: 'Введите старый пароль' }],
  password: [{ required: true, message: 'Введите новый пароль' }],
  confirmPassword: [{ required: true, message: 'Подтвердите пароль' }],
};

const isStrongPassword = (value: string): boolean => {
  const hasLength = value.length >= 6 && value.length <= 10;
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasDigit = /\d/.test(value);
  return hasLength && hasUppercase && hasLowercase && hasDigit;
};

const isPasswordStrong = computed(() => isStrongPassword(form.password));
const isConfirmPasswordStrong = computed(() => isStrongPassword(form.confirmPassword));

const showPasswordStrengthHint = computed(() => form.password.trim().length > 0);
const showPasswordsMatchHint = computed(
  () => form.password.trim().length > 0 && form.confirmPassword.trim().length > 0,
);

const isPasswordsMatch = computed(
  () => showPasswordsMatchHint.value && form.password === form.confirmPassword,
);

const passwordStrengthText = computed(() =>
  isPasswordStrong.value ? 'Сильный пароль' : 'Слабый пароль',
);
const passwordsMatchText = computed(() =>
  isPasswordsMatch.value ? 'Пароли совпадают' : 'Пароли не совпадают',
);

const passwordStrengthClass = computed(() => (isPasswordStrong.value ? 'is-success' : 'is-error'));
const passwordsMatchClass = computed(() => (isPasswordsMatch.value ? 'is-success' : 'is-error'));

const canSubmit = computed(
  () =>
    !oldPasswordError.value &&
    form.oldPassword.trim().length > 0 &&
    isPasswordStrong.value &&
    isConfirmPasswordStrong.value &&
    isPasswordsMatch.value,
);

const isWrongOldPasswordError = (message: string): boolean => {
  const normalized = message.toLowerCase();

  return (
    normalized.includes('текущий пароль') ||
    normalized.includes('неверн') ||
    normalized.includes('не верен')
  );
};

const clearOldPasswordError = (): void => {
  oldPasswordError.value = '';
};

watch(
  () => form.oldPassword,
  () => {
    clearOldPasswordError();
  },
);

const readApiErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  const candidates = ['message', 'detail', 'error', 'msg'];
  for (const key of candidates) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  if (Array.isArray(record.detail) && record.detail.every((item) => typeof item === 'string')) {
    return record.detail.filter(Boolean).join(' ');
  }
  return null;
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const onSubmit = async () => {
  clearOldPasswordError();
  submitLoading.value = true;
  try {
    const sessid = getBitrixSessid();
    if (!sessid) {
      throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
    }

    const response = await fetch(getApiUrl(CHANGE_PASSWORD_ENDPOINT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Bitrix-Csrf-Token': sessid },
      credentials: 'same-origin',
      body: JSON.stringify({
        old_password: form.oldPassword,
        password: form.password,
        confirm_password: form.confirmPassword,
        sessid: sessid,
      }),
    });

    const data = await parseResponseBody(response);

    if (!response.ok) {
      const fromBody = readApiErrorMessage(data);
      throw new Error(fromBody ?? `Ошибка смены пароля (${response.status})`);
    }

    const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    const msg = record?.msg;
    showStcSuccessNotification(
      typeof msg === 'string' && msg.trim() ? msg.trim() : 'Пароль успешно изменён',
    );
    updatePasswordChangedInfo(undefined, passwordSettingsRoot);
    resetFormAndHideMainModal();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Не удалось сохранить пароль';

    if (isWrongOldPasswordError(message)) {
      oldPasswordError.value = OLD_PASSWORD_ERROR_MESSAGE;
      return;
    }

    showStcErrorNotification(message);
  } finally {
    submitLoading.value = false;
  }
};

const resetFormAndHideMainModal = () => {
  form.oldPassword = '';
  form.password = '';
  form.confirmPassword = '';
  clearOldPasswordError();
  isOpen.value = false;
};

const open = () => {
  isOpen.value = true;
};

const close = () => {
  resetFormAndHideMainModal();
};

const renderPasswordIcon = (visible: boolean) =>
  h('img', {
    src: visible ? `${ICONS_BASE}/share-icons/eye-slash.svg` : `${ICONS_BASE}/share-icons/eye.svg`,
    alt: '',
    width: 24,
    height: 24,
  });

defineExpose({ open, close });
</script>

<style>
.password-change-lk-modal .ant-modal-content {
  width: 456px;
  padding: 40px;
  padding-bottom: 56px;
  border-radius: var(--stc-border-radius-md);
  background-color: #fff;
}

.password-change-lk-modal .ant-modal-close {
  width: 24px;
  height: 24px;
  top: 40px;
  right: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-change-lk-modal .ant-modal-close-x {
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-change-lk-modal__header {
  margin: 0;
}

.password-change-lk-modal .password-change-lk-modal__title {
  margin: 0;
  font-family: var(--stc-font-semibold);
  font-weight: 600;
  font-size: 20px;
  line-height: 0.8;
  letter-spacing: 0%;
  color: var(--stc-black);
}

.password-change-lk-modal__description {
  margin: 30px 0 0;
  font-family: var(--stc-font-regular);
  font-size: 12px;
  line-height: 153%;
  color: var(--stc-gray-400);
}

.password-change-lk-modal__form {
  margin-top: 31px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.password-change-lk-modal .ant-form-item {
  margin-bottom: 0px;

  input.ant-input {
    font-family: var(--stc-font-regular), sans-serif;
    font-weight: 400;
    font-size: 14px;
  }
}

.password-change-lk-modal__form.stc-ant-field-skin {
  .ant-input-affix-wrapper .ant-input,
  .ant-input-affix-wrapper .ant-input:focus,
  .ant-input-affix-wrapper .ant-input:focus-visible,
  .ant-input-affix-wrapper .ant-input.ant-input-focused {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
  }

  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper:focus-within,
  .ant-input-affix-wrapper.ant-input-affix-wrapper-focused {
    box-shadow: none !important;
  }
}

.password-change-lk-modal .ant-form-item:not(:first-of-type) {
  margin-top: 12px;
}

.password-change-lk-modal .ant-form-item-label {
  padding-bottom: 6px;
}

.password-change-lk-modal .ant-form-item-label > label {
  font-family: var(--stc-font-regular);
  font-weight: 400;
  font-size: 12px;
  line-height: 153%;
  letter-spacing: 0%;
  color: var(--stc-gray-400);
}

.password-change-lk-modal__hint {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--stc-font-medium);
  font-size: 12px;
  line-height: 153%;
}

.password-change-lk-modal__hint-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-change-lk-modal__hint-mark {
  display: block;
}

.password-change-lk-modal__hint.is-success {
  color: #52c41a;
}

.password-change-lk-modal__hint.is-error {
  color: var(--stc-red-600);
}

.password-change-lk-modal__submit.btn {
  height: 40px;
  padding: 8px 0;
  font-size: 14px;
  width: 100%;
  margin-top: 40px;
  font-family: var(--stc-font-medium), sans-serif;
  font-weight: 500;
  border-radius: 8px;
}
</style>
